/**
 * ⟦C52 · A-170 §1⟧ **La manche** : la mécanique du plateau, pure, sans DOM ni horloge.
 *
 * `blocs.ts` décide de la SUITE (ce qui tombe, quand, à quelle vitesse) et du BARÈME ; ce module
 * décide de la PHYSIQUE : où est chaque bloc à l'instant `t`, quel bloc répond aux touches, ce que
 * donnent ←, →, ↓ et un appui sur une colonne, et ce que devient le journal de placements. Le
 * composant React ne fait que dessiner cet état et lui passer les entrées.
 *
 * Pourquoi dans `shared/` plutôt que dans le client : c'est **la même physique** que celle que le
 * serveur suppose quand il juge qu'un placement est plausible (`verifierPlacements` : « pas avant
 * la chute rapide, pas après le sol »). Un seul code, donc un journal honnête passe toujours la
 * plausibilité, et la mesure de difficulté (joueurs simulés) joue sur le moteur du vrai jeu.
 *
 * ## Les règles du plateau
 *
 *  - Un bloc apparaît en haut, dans sa voie, à `apparitionMs`, et tombe à vitesse constante : il
 *    touche le sol à `apparitionMs + chuteMs`. Il se **pose** en touchant le sol, dans la voie où il
 *    est à cet instant.
 *  - **Le bloc actif** est le plus bas qui n'a pas encore touché le sol : c'est lui qui répond aux
 *    entrées. Hors doublet il n'y en a qu'un.
 *  - **←, →** : le bloc actif change de voie d'un cran (un cran de plus que le bord ne compte pas).
 *  - **Un appui sur une colonne** : le bloc y passe d'un coup ; un second appui sur la colonne où
 *    il est déjà le **lâche** (chute rapide jusqu'au sol). Au toucher, c'est le geste complet.
 *  - **↓ (maintenu)** : chute rapide ×`CHUTE_RAPIDE` pour le bloc qui était actif à l'appui, jamais
 *    pour celui qui apparaît ensuite (une touche gardée enfoncée ne lâche pas un piège).
 *  - Un bloc est **touché** dès qu'une entrée l'a déplacé ou lâché. C'est ce qui fait l'entrée du
 *    journal ; un piège s'évite en n'y touchant pas.
 */
import { CHUTE_RAPIDE, } from "./blocs.js";
export function creerManche(blocs, colonnes) {
    return {
        blocs,
        colonnes,
        tMs: 0,
        enJeu: blocs.map((b) => ({
            etat: "attente",
            voie: b.voie,
            progres: 0,
            touche: false,
            rapide: false,
            ok: null,
            poseAtMs: null,
        })),
        placements: [],
        basSur: null,
    };
}
/** Le bloc qui répond aux entrées : le plus bas parmi ceux qui tombent (le plus ancien en cas d'égalité). */
export function blocActif(m) {
    let meilleur = null;
    for (let i = 0; i < m.enJeu.length; i++) {
        const e = m.enJeu[i];
        if (e.etat !== "tombe")
            continue;
        if (meilleur === null || e.progres > m.enJeu[meilleur].progres)
            meilleur = i;
    }
    return meilleur;
}
/** ← / → : le bloc actif change de voie. Rend `true` s'il a bougé (un bord n'est pas un geste). */
export function deplacerBloc(m, delta) {
    const a = blocActif(m);
    if (a === null)
        return false;
    const e = m.enJeu[a];
    const voie = Math.max(0, Math.min(m.colonnes.length - 1, e.voie + delta));
    if (voie === e.voie)
        return false;
    e.voie = voie;
    e.touche = true;
    return true;
}
/**
 * Un appui sur une colonne : le bloc actif y passe ; sur la colonne où il est déjà, il est lâché.
 * Rend `true` si l'appui a eu un effet.
 */
export function allerEnVoie(m, voie) {
    const a = blocActif(m);
    if (a === null || !Number.isInteger(voie) || voie < 0 || voie >= m.colonnes.length)
        return false;
    const e = m.enJeu[a];
    if (e.voie === voie) {
        e.rapide = true;
    }
    else {
        e.voie = voie;
    }
    e.touche = true;
    return true;
}
/** ↓ enfoncé (`true`) ou relâché (`false`). Au repos, aucun bloc n'est lâché. */
export function lacherBloc(m, enfonce) {
    if (!enfonce) {
        m.basSur = null;
        return;
    }
    const a = blocActif(m);
    if (a === null)
        return;
    m.basSur = a;
    m.enJeu[a].touche = true;
}
/**
 * Fait avancer la manche de `dtMs`. Rend les événements de l'intervalle, dans l'ordre.
 *
 * L'instant où un bloc touche le sol est **calculé**, pas échantillonné : il vaut
 * `apparitionMs + chuteMs` à vitesse normale quelle que soit la cadence des images, et il ne
 * descend jamais sous `apparitionMs + chuteMs / CHUTE_RAPIDE`. C'est ce que `verifierPlacements`
 * vérifie côté serveur.
 */
export function avancerManche(m, dtMs) {
    const evenements = [];
    const t0 = m.tMs;
    const t1 = t0 + Math.max(0, dtMs);
    m.tMs = t1;
    const poses = [];
    for (const b of m.blocs) {
        const e = m.enJeu[b.i];
        if (e.etat === "attente" && t1 >= b.apparitionMs) {
            e.etat = "tombe";
            evenements.push({ type: "apparition", bloc: b.i, atMs: b.apparitionMs });
        }
        if (e.etat !== "tombe")
            continue;
        const debut = Math.max(t0, b.apparitionMs);
        const duree = t1 - debut;
        const rapide = e.rapide || m.basSur === b.i;
        const vitesse = (rapide ? CHUTE_RAPIDE : 1) / b.chuteMs;
        const reste = (1 - e.progres) / vitesse;
        if (duree >= reste) {
            e.progres = 1;
            poses.push({ i: b.i, atMs: Math.round(debut + reste) });
        }
        else {
            e.progres += duree * vitesse;
        }
    }
    poses.sort((x, y) => x.atMs - y.atMs || x.i - y.i);
    for (const { i, atMs } of poses) {
        const b = m.blocs[i];
        const e = m.enJeu[i];
        e.etat = "pose";
        e.poseAtMs = atMs;
        const piege = b.colonne === null;
        e.ok = piege ? !e.touche : m.colonnes[e.voie] === b.colonne;
        if (m.basSur === i)
            m.basSur = null;
        if (e.touche)
            m.placements.push({ bloc: i, colonne: m.colonnes[e.voie], atMs });
        evenements.push({
            type: piege ? (e.ok ? "piege_evite" : "piege_touche") : e.ok ? "pose_ok" : "pose_ratee",
            bloc: i,
            atMs,
        });
    }
    return evenements;
}
/** Tous les blocs ont touché le sol. */
export function toutPose(m) {
    return m.enJeu.every((e) => e.etat === "pose");
}
//# sourceMappingURL=manche.js.map